// First-use password prompt for magic-link customers.
//
// A customer arriving via a magic link is already signed in (the wizard-login
// exchange set their session cookie) but has no password yet. We invite them
// to set one so they can return later with email + password — but we NEVER
// block them: they clicked a link to check their quote, so the prompt is a
// skippable overlay on top of the already-loaded portal.
//
// Posts to POST /api/customer/set-password (session-authed, scrypt-hashed
// server-side). Reuses the login card styling for a consistent look.

import { api } from "@tkos/api-client";

/** Show the skippable "set your password" overlay. No-op if already shown. */
export function showSetPasswordPrompt(): void {
  if (document.getElementById("cust-setpw")) return;

  const overlay = document.createElement("div");
  overlay.id = "cust-setpw";
  overlay.className = "cust-setpw-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Set your password");

  const card = document.createElement("div");
  card.className = "cust-login-card cust-setpw-card";

  const heading = document.createElement("h1");
  heading.className = "cust-login-heading";
  heading.textContent = "Set your password";

  const blurb = document.createElement("p");
  blurb.className = "cust-login-blurb";
  blurb.textContent =
    "Set your password to access your portal anytime — or skip for now.";

  const form = document.createElement("form");
  form.className = "cust-login-form";
  form.noValidate = true;

  const pwLabel = document.createElement("label");
  pwLabel.htmlFor = "cust-setpw-password";
  pwLabel.textContent = "Password";
  const pw = document.createElement("input");
  pw.id = "cust-setpw-password";
  pw.type = "password";
  pw.autocomplete = "new-password";
  pw.placeholder = "At least 8 characters";
  pw.required = true;

  const confLabel = document.createElement("label");
  confLabel.htmlFor = "cust-setpw-confirm";
  confLabel.textContent = "Confirm password";
  const conf = document.createElement("input");
  conf.id = "cust-setpw-confirm";
  conf.type = "password";
  conf.autocomplete = "new-password";
  conf.placeholder = "Re-enter your password";
  conf.required = true;

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "cust-login-primary";
  submit.textContent = "Set password";

  const err = document.createElement("div");
  err.className = "cust-login-err";
  err.setAttribute("role", "alert");
  err.setAttribute("aria-live", "polite");

  form.append(pwLabel, pw, confLabel, conf, submit, err);

  const skip = document.createElement("button");
  skip.type = "button";
  skip.className = "cust-login-link cust-setpw-skip";
  skip.textContent = "Skip for now";

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") close();
  };
  function close(): void {
    overlay.remove();
    document.removeEventListener("keydown", onKey);
  }

  skip.addEventListener("click", close);

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const p = pw.value;
    err.textContent = "";
    if (p.length < 8) {
      err.textContent = "Password must be at least 8 characters.";
      return;
    }
    if (p !== conf.value) {
      err.textContent = "Those passwords don't match.";
      return;
    }
    submit.disabled = true;
    submit.textContent = "Saving…";
    api<{ ok: boolean; error?: string }>("/customer/set-password", {
      method: "POST",
      body: JSON.stringify({ password: p }),
    })
      .then((d) => {
        if (!d.ok) {
          err.textContent = d.error || "Couldn't save your password.";
          submit.disabled = false;
          submit.textContent = "Set password";
          return;
        }
        close();
      })
      .catch(() => {
        err.textContent = "Couldn't save your password. Please try again.";
        submit.disabled = false;
        submit.textContent = "Set password";
      });
  });

  card.append(heading, blurb, form, skip);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
  document.addEventListener("keydown", onKey);
  setTimeout(() => pw.focus(), 30);
}
